import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import RegionSelect from './RegionSelect';
import { useJazyk } from '../../context/LanguageContext';
import { API } from '../../lib/api';
import {
  DRUHY_KATALOGU, najdiDruh, prazdnyFormular, seskladejTelo, zkontroluj,
} from '../../lib/directoryKinds';

/**
 * Formulář, kterým členové posílají návrhy do katalogu.
 *
 * Návrh se nikdy nezveřejní sám – jde do fronty a někdo ho projde. Je to
 * napsané rovnou v okně, aby nikdo nečekal, že se to objeví hned, a nepodivil
 * se, když to tam za minutu není.
 */
export default function SuggestDialog({
  open,
  onOpenChange,
  countryIso2 = '',
  countryName = '',
  vychoziDruh = 'organizations',
}) {
  const { t, dvojjazycne } = useJazyk();
  const [druhKey, setDruhKey] = useState(vychoziDruh);
  const [zeme, setZeme] = useState([]);
  const [formular, setFormular] = useState(() => prazdnyFormular(najdiDruh(vychoziDruh), countryIso2));
  const [odesila, setOdesila] = useState(false);
  const [hotovo, setHotovo] = useState(false);

  const druh = najdiDruh(druhKey);

  useEffect(() => {
    if (!open) return;
    setDruhKey(vychoziDruh);
    setFormular(prazdnyFormular(najdiDruh(vychoziDruh), countryIso2));
    setHotovo(false);
    // Země načítáme až při otevření – ne při každém vykreslení stránky pod tím.
    if (zeme.length === 0) {
      axios.get(`${API}/geo/countries`).then((r) => setZeme(r.data)).catch(() => {});
    }
  }, [open, vychoziDruh, countryIso2]);   // eslint-disable-line react-hooks/exhaustive-deps

  const prepniDruh = (key) => {
    setDruhKey(key);
    // Políčka navíc se u druhů liší, tak se formulář založí znovu –
    // jinak by v něm zůstal termín akce u návrhu organizace.
    setFormular(prazdnyFormular(najdiDruh(key), formular.country_iso2));
  };

  const odesli = async () => {
    const problem = zkontroluj(t, druh, formular);
    if (problem) { toast.error(problem); return; }

    setOdesila(true);
    try {
      await axios.post(`${API}/directory/${druhKey}/submit`, seskladejTelo(druh, formular));
      setHotovo(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('navrh.chyba'));
    } finally {
      setOdesila(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="suggest-dialog">
        {hotovo ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 rounded-2xl bg-bloom-mint/20 flex items-center justify-center mx-auto">
              <Send className="w-5 h-5 text-bloom-violet" />
            </div>
            <h3 className="mt-4 font-heading text-xl font-bold text-bloom-text">
              {t('navrh.dikyTitulek')}
            </h3>
            <p className="mt-2 text-bloom-sub max-w-sm mx-auto">{t('navrh.dikyPopis')}</p>
            <Button onClick={() => onOpenChange(false)} className="mt-5 bg-bloom-violet hover:bg-bloom-violet/90">
              {t('navrh.zavrit')}
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {t('navrh.titulek', {
                  druh: t(druh?.klicJednotne || ''), zeme: countryName || '',
                })}
              </DialogTitle>
              <DialogDescription>{t('navrh.popisFormulare')}</DialogDescription>
            </DialogHeader>

            <div className="flex flex-wrap gap-2">
              {DRUHY_KATALOGU.map(({ key, icon: Icon }) => (
                <button key={key} type="button" onClick={() => prepniDruh(key)}
                        className={`px-3 py-1.5 rounded-lg text-sm inline-flex items-center gap-1.5 transition-colors ${
                          druhKey === key
                            ? 'bg-bloom-violet text-white'
                            : 'bg-white border border-border/50 text-bloom-sub hover:text-bloom-text'
                        }`}
                        data-testid={`suggest-kind-${key}`}>
                  <Icon className="w-3.5 h-3.5" />{t(`sekce.${key}`)}
                </button>
              ))}
            </div>
            <p className="text-xs text-bloom-sub/80 -mt-1">{t(druh?.klicPopis || '')}</p>

            <div className="space-y-3">
              <Pole label={`${t('navrh.nazev')} *`}>
                <Input value={formular.name} data-testid="suggest-name"
                       onChange={(e) => setFormular({ ...formular, name: e.target.value })} />
              </Pole>
              <Pole label={t('navrh.popisPole')}>
                <Textarea rows={3} value={formular.description}
                          onChange={(e) => setFormular({ ...formular, description: e.target.value })} />
              </Pole>

              <div className="grid sm:grid-cols-2 gap-3">
                <Pole label={`${t('navrh.zeme')} *`}>
                  <select value={formular.country_iso2} data-testid="suggest-country"
                          onChange={(e) => setFormular({ ...formular, country_iso2: e.target.value })}
                          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                    <option value="">{t('navrh.vyberte')}</option>
                    {zeme.map((z) => (
                      <option key={z.iso2} value={z.iso2}>
                        {dvojjazycne(z.name_cs, z.name_en)}
                      </option>
                    ))}
                  </select>
                </Pole>
                <Pole label={t('navrh.mesto')}>
                  <Input value={formular.city_name}
                         onChange={(e) => setFormular({ ...formular, city_name: e.target.value })} />
                </Pole>
              </div>

              <RegionSelect countryIso2={formular.country_iso2}
                            hodnota={formular.region_id}
                            onChange={(v) => setFormular({ ...formular, region_id: v })} />

              <Pole label={t('navrh.adresa')}>
                <Input value={formular.address}
                       onChange={(e) => setFormular({ ...formular, address: e.target.value })} />
              </Pole>

              <div className="grid sm:grid-cols-3 gap-3">
                <Pole label={t('navrh.web')}><Input value={formular.website}
                       onChange={(e) => setFormular({ ...formular, website: e.target.value })} /></Pole>
                <Pole label={t('navrh.email')}><Input value={formular.email}
                       onChange={(e) => setFormular({ ...formular, email: e.target.value })} /></Pole>
                <Pole label={t('navrh.telefon')}><Input value={formular.phone}
                       onChange={(e) => setFormular({ ...formular, phone: e.target.value })} /></Pole>
              </div>

              {druh?.pole.map((p) => (
                <Pole key={p.name} label={t(p.klic || '') + (p.povinne ? ' *' : '')}>
                  <PoleDetailu pole={p} t={t} hodnota={formular.details[p.name] || ''}
                               onChange={(v) => setFormular({
                                 ...formular, details: { ...formular.details, [p.name]: v },
                               })} />
                </Pole>
              ))}

              <Pole label={t('navrh.odkudVis')}>
                <Input value={formular.source} placeholder={t('navrh.odkudVisPriklad')}
                       onChange={(e) => setFormular({ ...formular, source: e.target.value })} />
              </Pole>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t('obecne.zrusit')}
              </Button>
              <Button onClick={odesli} disabled={odesila}
                      className="bg-bloom-violet hover:bg-bloom-violet/90" data-testid="suggest-submit">
                {odesila ? t('navrh.odesilam') : t('navrh.odeslat')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Pole({ label, children }) {
  return (
    <div>
      <Label className="text-sm text-bloom-text">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function PoleDetailu({ pole, hodnota, onChange, t }) {
  if (pole.typ === 'vyber') {
    return (
      <select value={hodnota} onChange={(e) => onChange(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
        <option value="">{t('navrh.vyberte')}</option>
        {/* Když má výběr klíč do slovníku, bere se překlad; jinak český text
            z konfigurace, ať se v nejhorším ukáže něco čitelného. */}
        {pole.moznosti.map(([v, l]) => (
          <option key={v} value={v}>{pole.klicMoznosti ? t(`${pole.klicMoznosti}.${v}`) : l}</option>
        ))}
      </select>
    );
  }
  if (pole.typ === 'datum') {
    return <Input type="datetime-local" value={hodnota} onChange={(e) => onChange(e.target.value)} />;
  }
  if (pole.typ === 'dlouhy') {
    return <Textarea rows={4} value={hodnota} onChange={(e) => onChange(e.target.value)} />;
  }
  if (pole.typ === 'seznam') {
    return <Input value={hodnota} placeholder={t('navrh.seznamNapoveda')}
                  onChange={(e) => onChange(e.target.value)} />;
  }
  return <Input value={hodnota} onChange={(e) => onChange(e.target.value)} />;
}
